import { formatCurrency } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, input, model } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { AsinType, InventoryConfig } from "@front/m19-api-client";
import { AccountSelectionService, AmazonAsinUrlPipe } from "@front/m19-services";
import {
  IButtonDirective,
  ICheckboxComponent,
  IIconComponent,
  ITableTdDirective,
  ITooltipDirective,
  ModalService,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import {
  SeedsEditionModalComponent,
  SeedsEditionModalData,
} from "@m19-board/catalog/seeds/seeds-edition-modal.component";
import { ProductDetailsComponent } from "@m19-board/product-analysis/product-details/product-details.component";
import { CatalogProductEx, ManagedCatalogPageService } from "@m19-board/services/managed-catalog-page.service";
import { switchMap, tap } from "rxjs";
import { ProductTagField } from "../catalog.component";
import { TooltipTruncatedDirective } from "../tooltipTruncated.directive";
import { AjaxResponse } from "rxjs/ajax";

@Component({
  selector: "[app-catalog-row]",
  imports: [
    AmazonAsinUrlPipe,
    ITableTdDirective,
    ICheckboxComponent,
    IButtonDirective,
    ITooltipDirective,
    TooltipTruncatedDirective,
    TranslocoDirective,
    IIconComponent,
  ],
  templateUrl: "./catalog-row.component.html",
  styles: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CatalogRowComponent {
  private readonly toastrService = inject(ToasterService);
  private readonly translocoService = inject(TranslocoService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly modalService = inject(ModalService);
  private readonly managedCatalogPageService = inject(ManagedCatalogPageService);

  product = input.required<CatalogProductEx>();
  isManaged = input<boolean>(true);
  currencyCode = input<string>();
  currency = input<string>();
  locale = input<string>();
  inventoryConfig = input<InventoryConfig>();
  isRowSelected = model<boolean>(false);

  readonly ProductTagField = ProductTagField;
  readonly isReadOnly = toSignal(this.accountSelectionService.readOnlyMode$);
  readonly accountMarketplace = toSignal(this.accountSelectionService.singleAccountMarketplaceSelection$, {
    requireSync: true,
  });

  formattedCogs = computed(() => {
    const cog = this.product().costOfGoods;
    const currencyCode = this.currencyCode();
    const currency = this.currency();
    const locale = this.locale();

    if (!cog || !currencyCode || !currency || !locale) return "-";
    return formatCurrency(cog, locale, currency, currencyCode, "1.0-2");
  });

  readonly kwSeeds = computed(() => this.product().keywordSeeds);
  readonly productSeeds = computed(() => this.product().productSeeds);
  readonly kwBlacklist = computed(() => this.product().keywordBlacklist);
  readonly productBlacklist = computed(() => this.product().productBlacklist);

  readonly seedsLength = computed(() => (this.kwSeeds()?.length ?? 0) + (this.productSeeds()?.length ?? 0));
  readonly blacklistLength = computed(() => (this.kwBlacklist()?.length ?? 0) + (this.productBlacklist()?.length ?? 0));

  readonly isSeedsDisabled = computed(() => this.isReadOnly() || this.product().asinType == AsinType.PARENT);

  readonly seedsTooltip = computed(() => {
    if (this.isSeedsDisabled()) return this.translocoService.translate("catalog-page.this_asin_is_ineligible");
    if (!this.kwSeeds()?.length && !this.productSeeds()?.length) return null;
    return this.translocoService.translate("catalog-page.n_whitelisted_seeds", [
      this.kwSeeds()!.length,
      this.productSeeds()!.length,
    ]);
  });

  readonly blacklistTooltip = computed(() => {
    if (this.isSeedsDisabled()) return this.translocoService.translate("catalog-page.this_asin_is_ineligible");
    if (!this.kwBlacklist()?.length && !this.productBlacklist()?.length) return null;
    return this.translocoService.translate("catalog-page.n_blacklisted_seeds", [
      this.kwBlacklist()!.length,
      this.productBlacklist()!.length,
    ]);
  });

  editCogs(product: CatalogProductEx) {
    const currentCogs = [{ asin: product.asin!, costOfGoods: product.costOfGoods! }];
    this.managedCatalogPageService
      .openCogsUpdateModal(product)
      .pipe(
        tap((cogValue) => {
          this.managedCatalogPageService.setCogsState([{ asin: product.asin!, costOfGoods: cogValue! }]);
        }),
        switchMap((cogValue) => {
          return this.managedCatalogPageService.updateProductCogs(product.asin!, cogValue);
        }),
      )
      .subscribe({
        error: (error: AjaxResponse<Error>) => {
          this.toastrService.error(error.response.message);
          this.managedCatalogPageService.setCogsState(currentCogs);
        },
      });
  }

  openSeedsEditionModal(whitelist: boolean) {
    if (this.isSeedsDisabled()) return;
    this.modalService.openModal<SeedsEditionModalData, void>(SeedsEditionModalComponent, {
      size: "lg",
      height: "fill",
      modalTitle: whitelist
        ? this.translocoService.translate("seeds-edition-modal.add_keywords_for_asin", [this.product().asin!])
        : this.translocoService.translate("seeds-edition-modal.blacklist_for_asin", [this.product().asin!]),
      data: {
        accountId: this.accountMarketplace().accountId,
        marketplace: this.accountMarketplace().marketplace,
        asin: this.product().asin!,
        keywordTargetingItems: [...(this.product().keywordSeeds ?? []), ...(this.product().keywordBlacklist ?? [])],
        productTargetingItems: [...(this.product().productSeeds ?? []), ...(this.product().productBlacklist ?? [])],
        whitelist,
      },
    });
  }

  editProductTag(field: ProductTagField) {
    if (this.isReadOnly()) return;
    const currentValue = this.product()[field === ProductTagField.FIELD1 ? "productTag1" : "productTag2"];

    this.managedCatalogPageService
      .openProductTagEditionModal([this.product().asin!], field, currentValue)
      .pipe(
        tap((value) => {
          this.managedCatalogPageService.setProductTagsState([this.product().asin!], field, value);
        }),
        switchMap((value) => {
          return this.managedCatalogPageService.updateProductTags([this.product().asin!], field, value);
        }),
      )
      .subscribe({
        error: (error) => {
          this.toastrService.error(error.message);
          this.managedCatalogPageService.restoreProductTagsState(
            [{ asin: this.product().asin!, [field]: currentValue }],
            field,
          );
        },
      });
  }

  openStrategiesModal() {
    if (!this.product().strategies?.length) return;
    this.modalService.openModal(ProductDetailsComponent, {
      modalTitle: this.translocoService.translate("catalog-row.number_of_active_strategies_with_asin", {
        asin: this.product().asin,
      }),
      data: {
        strategies: this.product().strategies,
        asinnumber: this.product().asin!,
        marketplace: this.accountMarketplace().marketplace,
      },
    });
  }
}
